import React from 'react';

import CourseContactLinks from './CourseContactLinks';
import CourseHero from './CourseHero';
import CourseStateToggles from './CourseStateToggles';
import ShareCourseButton from './ShareCourseButton';
import { CourseMinifiedDTO } from '../../services/apiClient';
import { formatDistance } from '../../utils/geoUtils';
import ClosedBadge from '../common/ClosedBadge';

// The one place two course ids are compared. Case-insensitive per CLAUDE.md — GUID casing is not
// guaranteed to survive a round trip, and a pin click hands the id back through Mapbox's
// serialized feature properties rather than from the original object.
export const isSameCourse = (
  a: string | undefined,
  b: string | undefined,
): boolean => a != null && b != null && a.toLowerCase() === b.toLowerCase();

// The origin sits at index 0 of the array, but position is not identity — compare ids so a card
// cannot be mislabelled if the ordering ever changes. originKey is the origin course's id, or the
// literal 'user' when the list is built around the visitor's own location, which matches nothing.
export const isOriginCourse = (
  id: string | undefined,
  originKey: string,
): boolean => isSameCourse(id, originKey);

interface CourseCardBaseProps {
  course: CourseMinifiedDTO;
  // Undefined in the A to Z list when the visitor's location is unknown: no distance pill.
  distanceMeters?: number;
  isSelected: boolean;
  isOrigin: boolean;
  onClick: () => void;
  // Sizing and scroll-snap only — the nearby strip needs a fixed-width, snapping card while the
  // full-page grid lets its columns set the width. Nothing that changes how the card *looks*
  // belongs here: the two surfaces are meant to render an identical card.
  className?: string;
}

// The state toggles ride along by default, which is what the list view and the strip want. The
// Explore drawer turns them off for its "next nearby" card: that card sits a few rows below the
// drawer's own Played and Bucket List rows, and two sets of the same two controls that far apart
// is a trap — nothing tells the visitor which club each set acts on.
//
// A union rather than independent optional props, so the compiler enforces the pairing. With them
// all merely optional, a surface that asks for the toggles but forgets a handler renders a card
// with no controls at all, silently and with nothing to say why.
type CourseCardActionProps =
  | {
      showActions?: true;
      isPlayed: boolean;
      isBucketListed: boolean;
      onTogglePlayed: () => void;
      onBucketList: () => void;
      onRequestToPlay: () => void;
    }
  | {
      showActions: false;
      isPlayed?: never;
      isBucketListed?: never;
      onTogglePlayed?: never;
      onBucketList?: never;
      onRequestToPlay?: never;
    };

type CourseCardProps = CourseCardBaseProps & CourseCardActionProps;

// The full course card, rendered by the List view from 640 px up. The phone strip and the
// drawer's next-nearby row use the smaller CourseCompactCard instead; the parts the two share
// (CourseHero, CourseStateToggles) are components, so they cannot drift apart.
//
// It is laid out as the detail drawer's club-details panel, because that is what it was asked to
// look like: hero, region pill, name, an address row and a phone row each behind their own icon, a
// full-width Share, then the two member-state checkboxes under a divider. CourseHero and
// CourseStateToggles are the shared pieces, so "the card looks like the drawer" is enforced by
// both rendering the same components rather than by two copies of the markup being kept in step.
//
// The card is an <article>, not a <button>. A button may not contain a link or another button, and
// this card holds map and tel: links, a Share button and two checkboxes — so the hero-and-name
// block is the clickable "select this course" control and everything from the address row down is
// its sibling.
//
// The address used to sit inside that button, which made it part of the card's tap target but also
// made it impossible to link. It is a map link now, so it moved out to join the phone row. The
// card still selects on its hero, its region pill and its name, which is the part people aim at.
const CourseCard: React.FC<CourseCardProps> = ({
  course,
  distanceMeters,
  isSelected,
  isOrigin,
  onClick,
  isPlayed = false,
  isBucketListed = false,
  onTogglePlayed,
  onBucketList,
  showActions = true,
  className = '',
}) => {
  const locationLine = [course.stateName, course.countryName]
    .filter(Boolean)
    .join(', ');

  return (
    <article
      className={`flex flex-col overflow-hidden rounded-2xl border bg-gradient-to-b from-charcoal-light to-charcoal shadow-lg transition-all ${
        isSelected
          ? 'border-primary shadow-[0_0_16px_rgba(187,170,100,0.45)]'
          : 'border-white/10 hover:border-primary/60 hover:shadow-[0_0_12px_rgba(187,170,100,0.25)]'
      } ${className}`}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col text-left"
      >
        <CourseHero
          course={course}
          distanceLabel={
            isOrigin
              ? 'This course'
              : distanceMeters != null
                ? formatDistance(distanceMeters)
                : null
          }
        />

        {/* pt-8 clears the logo plate, which hangs 1.25rem below the hero. Same figure as the
            drawer. */}
        <div className="px-4 pt-8">
          <div className="flex flex-wrap items-center gap-1.5">
            {locationLine && (
              <span className="inline-block rounded border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                {locationLine}
              </span>
            )}
            <ClosedBadge
              closed={course.closed}
              closedYear={course.closedYear}
              closedMonth={course.closedMonth}
              className="py-1"
            />
          </div>

          <h3 className="mt-2 line-clamp-2 text-lg font-bold leading-snug text-white">
            {course.name}
          </h3>
        </div>
      </button>

      <div className="flex flex-col px-4 pb-4">
        {/* Outside the select-this-course button on purpose: a <button> may not contain an <a>,
            and both of these have to be independently tappable, one to open a map and one to
            dial. Same component as the detail drawer, so the two surfaces cannot drift apart on
            what these rows do or how they look. */}
        <CourseContactLinks course={course} className="mt-2" />

        {/* Share is on the card as well as the drawer, so a course can be shared straight from
            the card grid without opening it first. ShareCourseButton removes itself when the
            browser can neither share nor copy. */}
        <ShareCourseButton course={course} className="mt-3 w-full" />
      </div>

      {/* Same divider the drawer puts between its sections. mt-auto pins the state block to the
          bottom of the card, so a grid row of cards with different address lengths still lines
          their checkboxes up. */}
      {showActions !== false && onTogglePlayed && onBucketList && (
        <div className="mt-auto">
          <div className="h-2 shrink-0 bg-black/25" />
          <CourseStateToggles
            idPrefix={`course-${course.id}`}
            isPlayed={isPlayed}
            isBucketListed={isBucketListed}
            onTogglePlayed={onTogglePlayed}
            onBucketList={onBucketList}
            className="p-3"
          />
        </div>
      )}
    </article>
  );
};

export default CourseCard;
